<template>
  <div class="documents-page">
    <PageContent>
      <section class="documents-panel">
        <header class="documents-toolbar">
          <label class="documents-search">
            <i class="fas fa-search" aria-hidden="true"></i>
            <input
              v-model.trim="searchQuery"
              type="search"
              placeholder="Rechercher un document"
              aria-label="Rechercher un document"
            >
          </label>

          <button
            v-if="hasEditionRights"
            type="button"
            class="documents-add-button"
            @click="addEvent"
          >
            <i class="fas fa-plus-circle" aria-hidden="true"></i>
            Ajouter un document
          </button>
          <nav class="documents-tabs" aria-label="Statut des documents">
            <button
              v-for="tab in tabs"
              :key="tab.key"
              type="button"
              class="documents-tabs__item"
              :class="{ 'documents-tabs__item--active': activeTab === tab.key }"
              @click="selectTab(tab.key)"
            >
              {{ tab.label }}
            </button>
          </nav>
        </header>

        <div v-if="loading" class="documents-state">
          <i class="fas fa-spinner fa-spin" aria-hidden="true"></i>
          Chargement des documents…
        </div>

        <div v-else-if="loadError" class="documents-state documents-state--error">
          <span>{{ loadError }}</span>
          <button type="button" @click="getEvents">Réessayer</button>
        </div>

        <div v-else class="documents-table-scroll">
          <table class="documents-table">
            <thead>
              <tr>
                <th class="documents-column--preview">Aperçu</th>
                <th>Titre du document / dossier</th>
                <th class="documents-column--reads">Lectures</th>
                <th class="documents-column--delete">
                  <span class="sr-only">Actions de suppression</span>
                  <i class="far fa-trash-alt" aria-hidden="true"></i>
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="event in filteredEvents">
                <tr
                  :key="event.uuid"
                  :class="{ 'documents-row--editable': hasEditionRights }"
                  :tabindex="hasEditionRights ? 0 : null"
                  @click="editDoc(event)"
                  @keydown.enter.self="editDoc(event)"
                >
                  <td class="documents-column--preview">
                    <button
                      type="button"
                      class="documents-icon-button documents-preview-button"
                      :title="isPreviewOpen(event) ? 'Masquer l’aperçu' : 'Afficher l’aperçu'"
                      :aria-expanded="isPreviewOpen(event) ? 'true' : 'false'"
                      @click.stop="togglePreview(event)"
                    >
                      <i class="far fa-eye" aria-hidden="true"></i>
                      <span class="sr-only">Aperçu de {{ event.title }}</span>
                    </button>
                  </td>
                  <td>
                    <span class="documents-title" :title="event.title">{{ event.title || '—' }}</span>
                  </td>
                  <td class="documents-column--reads">{{ consultationCount(event) }}</td>
                  <td class="documents-column--delete">
                    <button
                      v-if="hasEditionRights"
                      type="button"
                      class="documents-icon-button documents-delete-button"
                      :disabled="isOperationPending(event)"
                      :title="activeTab === 'active' ? 'Retirer le document' : 'Supprimer définitivement le document'"
                      @click.stop="confirmDelete(event)"
                    >
                      <i class="far fa-trash-alt" aria-hidden="true"></i>
                      <span class="sr-only">
                        {{ activeTab === 'active' ? 'Retirer' : 'Supprimer définitivement' }} {{ event.title }}
                      </span>
                    </button>
                    <span v-else>—</span>
                  </td>
                </tr>
                <tr v-if="isPreviewOpen(event)" :key="`${event.uuid}-preview`" class="documents-preview-row">
                  <td colspan="4">
                    <ListRowDetail
                      :item="event"
                      :uuid-copy-status="uuidCopyStatusFor(event.uuid)"
                      @copy-uuid="copyEventUuid"
                    />
                  </td>
                </tr>
              </template>

              <tr v-if="!filteredEvents.length">
                <td colspan="4" class="documents-empty">
                  {{ emptyStateMessage }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <footer v-if="!loading && !loadError" class="documents-footer">
          Affichage de {{ filteredEvents.length }} document{{ filteredEvents.length > 1 ? 's' : '' }}
        </footer>
      </section>
    </PageContent>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { http } from '@/services/api.service'
import PageContent from '@/components/PageContent.vue'
import ActiveContentRemovalModal from '@/pages/Contents/Modals/ActiveContentRemovalModal.vue'
import OneEvent from './Modals/One.vue'
import ListRowDetail from './ListRowDetail.vue'

const TABS = [
  { key: 'active', label: 'Activés' },
  { key: 'disabled', label: 'Désactivés' }
]

export default {
  name: 'app-documents-list',
  components: {
    PageContent,
    ListRowDetail
  },
  data () {
    return {
      tabs: TABS,
      activeTab: 'active',
      searchQuery: '',
      events: [],
      loading: false,
      loadError: '',
      loadRequestId: 0,
      expandedUuid: null,
      deletingUuid: null,
      disablingUuid: null,
      editorModalOpen: false,
      removalModalOpen: false,
      uuidCopyStatus: 'idle',
      uuidCopyTarget: null,
      uuidCopyTimer: null
    }
  },
  computed: {
    ...mapGetters([
      'domain',
      'roles'
    ]),
    hasEditionRights () {
      return this.roles.some(role => ['admin', 'director', 'community-manager'].includes(role))
    },
    filteredEvents () {
      const query = this.normalizeSearchValue(this.searchQuery)
      if (!query) return this.events

      return this.events.filter(event => this.eventSearchValue(event).includes(query))
    },
    emptyStateMessage () {
      if (this.searchQuery) return 'Aucun document ne correspond à votre recherche.'
      return this.activeTab === 'active'
        ? 'Aucun document activé.'
        : 'Aucun document désactivé.'
    }
  },
  watch: {
    domain () {
      this.expandedUuid = null
      this.getEvents()
    }
  },
  mounted () {
    this.getEvents()
  },
  beforeDestroy () {
    this.clearUuidCopyTimer()
  },
  methods: {
    async getEvents () {
      const requestId = ++this.loadRequestId
      this.loading = true
      this.loadError = ''

      try {
        const { data } = await http.get('/events', {
          params: {
            status: this.activeTab,
            sort: '-event_date'
          }
        })
        if (requestId !== this.loadRequestId) return

        this.events = Array.isArray(data)
          ? data
          : (data && Array.isArray(data.items) ? data.items : [])
        if (!this.events.some(event => event.uuid === this.expandedUuid)) {
          this.expandedUuid = null
        }
      } catch (error) {
        if (requestId !== this.loadRequestId) return
        this.events = []
        this.loadError = 'Impossible de récupérer les documents pour le moment.'
      } finally {
        if (requestId === this.loadRequestId) this.loading = false
      }
    },
    selectTab (tab) {
      if (tab === this.activeTab) return
      this.activeTab = tab
      this.expandedUuid = null
      this.getEvents()
    },
    normalizeSearchValue (value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase('fr')
        .trim()
    },
    eventSearchValue (event) {
      const files = Array.isArray(event.files) ? event.files : []
      return this.normalizeSearchValue([
        event.uuid,
        event.title,
        this.stripHtml(event.description),
        ...files.map(file => file && file.name),
        ...this.groupLabels(event)
      ].filter(Boolean).join(' '))
    },
    stripHtml (value) {
      return String(value || '').replace(/<[^>]*>/g, ' ')
    },
    groupLabels (event) {
      const notifications = Array.isArray(event.notifications) ? event.notifications : []
      return [...new Set(notifications
        .map(notification => notification && notification.group && notification.group.label)
        .filter(Boolean))]
    },
    consultationCount (event) {
      const count = Number(event && event.consultations_count)
      return Number.isFinite(count) && count >= 0 ? count : 0
    },
    isPreviewOpen (event) {
      return Boolean(event && event.uuid === this.expandedUuid)
    },
    togglePreview (event) {
      this.expandedUuid = this.isPreviewOpen(event) ? null : event.uuid
    },
    isOperationPending (event) {
      return Boolean(event && [this.deletingUuid, this.disablingUuid].includes(event.uuid))
    },
    addEvent () {
      if (!this.hasEditionRights || this.editorModalOpen) return
      this.openEventModal()
    },
    editDoc (event) {
      if (!this.hasEditionRights || this.editorModalOpen) return
      this.openEventModal(event)
    },
    openEventModal (currentEvent = null) {
      const modal = this.$buefy.modal.open({
        parent: this,
        component: OneEvent,
        hasModalCard: true,
        props: { currentEvent },
        width: 960,
        destroyOnHide: false,
        ariaRole: 'dialog',
        ariaModal: true,
        events: {
          created: (document, metadata = {}) => this.handleDocumentSaved(document, metadata, true),
          updated: (document, metadata = {}) => this.handleDocumentSaved(document, metadata, false)
        }
      })

      this.editorModalOpen = true
      modal.$once('close', () => {
        this.editorModalOpen = false
      })
    },
    handleDocumentSaved (document, metadata, isNew) {
      const isEnabled = metadata && Object.prototype.hasOwnProperty.call(metadata, 'isEnabled')
        ? metadata.isEnabled
        : this.toBoolean(document && document.is_enabled)
      const targetTab = isEnabled ? 'active' : 'disabled'

      this.$buefy.notification.open({
        duration: 3000,
        message: `Le document <strong>${document.title}</strong> a bien été ${isNew ? 'créé' : 'modifié'}.`,
        position: 'is-bottom-right',
        type: 'is-success',
        hasIcon: false
      })

      if (this.activeTab === targetTab) this.getEvents()
      else this.selectTab(targetTab)
    },
    toBoolean (value) {
      return [true, 1, '1', 'true'].includes(value)
    },
    uuidCopyStatusFor (uuid) {
      return this.uuidCopyTarget === uuid ? this.uuidCopyStatus : 'idle'
    },
    async copyEventUuid (uuid) {
      if (!uuid) return

      try {
        if (navigator.clipboard && window.isSecureContext) {
          try {
            await navigator.clipboard.writeText(uuid)
          } catch (error) {
            this.copyWithFallback(uuid)
          }
        } else {
          this.copyWithFallback(uuid)
        }
        this.setUuidCopyStatus(uuid, 'copied')
      } catch (error) {
        this.setUuidCopyStatus(uuid, 'error')
      }
    },
    copyWithFallback (value) {
      const textarea = document.createElement('textarea')
      textarea.value = value
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      let copied = false
      try {
        textarea.select()
        copied = document.execCommand('copy')
      } finally {
        document.body.removeChild(textarea)
      }
      if (!copied) throw new Error('Clipboard copy failed')
    },
    setUuidCopyStatus (uuid, status) {
      this.clearUuidCopyTimer()
      this.uuidCopyTarget = uuid
      this.uuidCopyStatus = status
      this.uuidCopyTimer = window.setTimeout(() => {
        this.uuidCopyStatus = 'idle'
        this.uuidCopyTarget = null
        this.uuidCopyTimer = null
      }, 2500)
    },
    clearUuidCopyTimer () {
      if (this.uuidCopyTimer !== null) {
        window.clearTimeout(this.uuidCopyTimer)
        this.uuidCopyTimer = null
      }
    },
    confirmDelete (event) {
      if (!this.hasEditionRights || this.isOperationPending(event)) return
      if (this.activeTab === 'active') {
        this.openActiveDocumentRemoval(event)
        return
      }
      this.confirmPermanentDelete(event)
    },
    openActiveDocumentRemoval (event) {
      if (this.removalModalOpen) return
      const modal = this.$buefy.modal.open({
        parent: this,
        component: ActiveContentRemovalModal,
        hasModalCard: true,
        width: 600,
        props: {
          title: event.title || '',
          contentType: 'document'
        },
        events: {
          close: () => modal.close(),
          disable: () => {
            modal.close()
            this.disableDocument(event)
          },
          delete: () => {
            modal.close()
            this.confirmPermanentDelete(event)
          }
        }
      })

      this.removalModalOpen = true
      modal.$once('close', () => {
        this.removalModalOpen = false
      })
    },
    confirmPermanentDelete (event) {
      this.$buefy.dialog.confirm({
        title: 'Suppression définitive',
        message: `Êtes-vous sûr de vouloir supprimer définitivement « ${event.title} » ? Cette action est irréversible.`,
        cancelText: 'Annuler',
        confirmText: 'Supprimer définitivement',
        type: 'is-danger',
        onConfirm: () => this.deleteDocument(event)
      })
    },
    async disableDocument (event) {
      this.disablingUuid = event.uuid
      try {
        await http.post(`/events/${event.uuid}/disable`)
        this.$buefy.notification.open({
          duration: 3000,
          message: `Le document <strong>${event.title}</strong> a bien été désactivé.`,
          position: 'is-bottom-right',
          type: 'is-success',
          hasIcon: false
        })
        this.selectTab('disabled')
      } catch (error) {
        this.notifyError('Impossible de désactiver ce document.')
      } finally {
        this.disablingUuid = null
      }
    },
    async deleteDocument (event) {
      this.deletingUuid = event.uuid
      try {
        await http.delete(`/events/${event.uuid}`)
        this.events = this.events.filter(document => document.uuid !== event.uuid)
        if (this.expandedUuid === event.uuid) this.expandedUuid = null
        this.$buefy.notification.open({
          duration: 3000,
          message: `Le document <strong>${event.title}</strong> a bien été supprimé définitivement.`,
          position: 'is-bottom-right',
          type: 'is-success',
          hasIcon: false
        })
      } catch (error) {
        this.notifyError('Impossible de supprimer définitivement ce document.')
      } finally {
        this.deletingUuid = null
      }
    },
    notifyError (message) {
      this.$buefy.notification.open({
        duration: 3500,
        message,
        position: 'is-bottom-left',
        type: 'is-warning',
        hasIcon: true,
        icon: 'server'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.documents-page {
  min-height: 100%;
  background: #f4f6f9;
}

.documents-panel {
  overflow: hidden;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
}

.documents-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 18px 24px;
  padding: 22px 28px;
}

.documents-search {
  display: flex;
  flex: 1 1 28rem;
  align-items: center;
  gap: 10px;
  max-width: 42rem;
  min-height: 46px;
  padding: 0 14px;
  color: #64748b;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
}

.documents-search:focus-within {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.14);
}

.documents-search input {
  flex: 1 1 auto;
  min-width: 0;
  padding: 0;
  font-size: 14px;
  background: transparent;
  border: 0;
  box-shadow: none;
  outline: none;
}

.documents-add-button {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
  min-height: 46px;
  padding: 12px 18px;
  color: #fff;
  font-size: 14px;
  font-weight: 400;
  background: #2563eb;
  border: 0;
  border-radius: 10px;
}

.documents-add-button:hover {
  background: #1d4ed8;
}

.documents-tabs {
  display: flex;
  width: fit-content;
  margin-left: auto;
  padding: 4px;
  background: #f3f4f6;
  border-radius: 999px;
}

.documents-tabs__item {
  padding: 10px 22px;
  color: #4b5563;
  font-size: 14px;
  font-weight: 400;
  white-space: nowrap;
  background: transparent;
  border: 0;
  border-radius: 999px;
}

.documents-tabs__item--active {
  color: #fff;
  background: #2563eb;
  box-shadow: 0 2px 5px rgba(37, 99, 235, 0.25);
}

.documents-state,
.documents-empty {
  padding: 48px;
  color: #6b7280;
  text-align: center;
}

.documents-state--error {
  color: #b91c1c;
}

.documents-state--error button {
  margin-left: 12px;
  color: #2563eb;
}

.documents-table-scroll {
  overflow-x: auto;
}

.documents-table {
  width: 100%;
  min-width: 720px;
  table-layout: fixed;
  border-spacing: 0;
  border-collapse: separate;
}

.documents-table th {
  padding: 12px 14px;
  color: #4b5563;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-align: left;
  text-transform: uppercase;
  background: #f8fafc;
}

.documents-table td {
  height: 64px;
  padding: 11px 14px;
  color: #374151;
  font-size: 14px;
  line-height: 1.25rem;
  vertical-align: middle;
  border-top: 1px solid #edf0f3;
}

.documents-table tbody tr:not(.documents-preview-row):hover {
  background: #f8fafc;
}

.documents-row--editable {
  cursor: pointer;
}

.documents-row--editable:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: -2px;
}

.documents-column--preview,
.documents-column--reads,
.documents-column--delete {
  width: 110px;
  text-align: center !important;
}

.documents-column--delete {
  width: 90px;
}

.documents-title {
  display: -webkit-box;
  font-size: 14px;
  max-height: 2.5rem;
  overflow: hidden;
  line-height: 1.25rem;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.documents-icon-button {
  font-size: 17px;
  background: transparent;
  border: 0;
}

.documents-table td.documents-column--preview,
.documents-table td.documents-column--delete {
  padding: 0;
}

.documents-table td.documents-column--preview .documents-icon-button,
.documents-table td.documents-column--delete .documents-icon-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 64px;
}

.documents-preview-button {
  color: #9ca3af;
}

.documents-preview-button:hover,
.documents-preview-button[aria-expanded='true'] {
  color: #2563eb;
}

.documents-delete-button {
  color: #ef4444;
}

.documents-delete-button:disabled {
  color: #d1d5db;
  cursor: wait;
}

.documents-preview-row td {
  height: auto;
  padding: 0 28px;
  background: #f8fafc;
}

.documents-footer {
  padding: 12px 24px;
  color: #6b7280;
  font-size: 12px;
  text-align: right;
  border-top: 1px solid #edf0f3;
}

@media (max-width: 700px) {
  .documents-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .documents-search {
    flex-basis: auto;
    max-width: none;
  }

  .documents-add-button {
    justify-content: center;
  }

  .documents-tabs {
    align-self: flex-end;
    max-width: 100%;
    margin-left: 0;
    overflow-x: auto;
  }
}
</style>
